
import {
    AfterViewInit,
    Component,
    computed,
    ElementRef,
    HostListener,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
    Type,
    ViewChild,
    ChangeDetectionStrategy
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import Extent from "@arcgis/core/geometry/Extent";
import Polygon from "@arcgis/core/geometry/Polygon";
import { MapAbortDrawComponent } from "@components/map/components/menu/map-abort-draw/map-abort-draw.component";
import { MapCollectorComponent } from "@components/map/components/menu/map-collector/map-collector.component";
import { MapDrawComponent } from "@components/map/components/menu/map-draw/map-draw.component";
import { MapFastighetComponent } from "@components/map/components/menu/map-fastighet/map-fastighet.component";
import { MapFullscreenComponent } from "@components/map/components/menu/map-fullscreen/map-fullscreen.component";
import { MapLayersComponent } from "@components/map/components/menu/map-layers/map-layers.component";
import { MapMeasureComponent } from "@components/map/components/menu/map-measure/map-measure.component";
import { MapComponent } from "@components/map/map.component";
import { debounce, DebouncedFunc, isNil, without } from "lodash";
import { BehaviorSubject, Observable, ReplaySubject, Subject } from "rxjs";
import {
    combineLatestWith,
    filter,
    take,
    takeUntil,
    withLatestFrom
} from "rxjs/operators";
import { IDrawGroup, MapDrawService } from "~app/common/components/map/components/menu/map-draw/map-draw.service";
import { MapImportService } from "~app/common/components/map/components/menu/map-file-import/map-import.service";
import { IHitItem, IMapComponent, IMapMouse } from "~app/common/components/map/map.models";
import {
    GeometryType,
    IFilterParams,
    IMapDrawLayer,
    IMapGeometry,
    IMapGeometryWicket,
    IMapMediaLayerContainer,
    IMapMediaLayerImage,
    IMapSettings,
    IRenderParams
} from "~app/common/components/map/map.settings";
import { SpatialService } from "~app/common/components/map/services/spatial.service";
import { TerraformerService } from "~app/common/components/map/services/terraformer.service";
import {
    CollectorImportKalla,
    KartaFeaturelayers,
    KartgeometriKategori,
    Planeringsstatus,
    VirkeskopAffarsformer,
    VkKartaLayerNames
} from "~app/common/models/constants";
import { EntityDTO } from "~app/common/models/entity-models";
import { FeatureService } from "~app/common/services/feature.service";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IEventData, IKartgeometriData, IKartgeometriEvent, KartgeometriPopupComponent } from "~app/features/map/popups/kartgeometri-popup/kartgeometri-popup.component";
import { IKartgeometri, Kartgeometri } from "~app/features/map/popups/kartgeometri-popup/kartgeometri.model";
import { MapEvents } from "~app/features/planera/events/map-events";
import { PlaneraKartaIcons } from "~app/features/planera/planera-karta/planera-karta-icons";
import { Kartgeometrityper } from "~app/features/planera/services/karta.service";
import { canEdit } from "~app/role-permissions";
import { AddOrUpdateGeometryCommand } from "../../commands/karta/AddOrUpdateGeometryCommand";
import { DeleteGeometryCommand } from "../../commands/karta/DeleteGeometryCommand";
import { VirkeskopDokumentInfoDTO } from "../../dto/dokument/VirkeskopDokumentInfoDTO";
import { ICollectorSubtyper } from "../../models/atgard-collector-models";
import { Atgard, VirkeskopsatgardKompatibilitet } from "../../models/atgard-models";
import {
    Geometri,
    Geometrier,
    GeometryUpdate,
    ICollectorGeometriWrapper,
    ICollectorImported,
    IMapGeometryLayer,
    ISkogsvardVirtualLayer
} from "../../models/karta-models";
import { Kontrakt } from "../../models/kontrakt-models";
import { Atgardstyper } from "../../models/virkeskopAtgardstyper";
import { AllSkogsvardTradslagService } from "../../services/all-skogsvard-tradslag.service";
import { AvtalMediatorService } from "../../services/avtal-mediator.service";
import { KartaService } from "../../services/karta.service";
import { KontraktCommandService } from "../../services/kontrakt-command.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { KontraktRitunderlagService } from "../../services/kontrakt-ritunderlag.service";
import { GeometryClientValidationService } from "../../services/validation/geometry-client-validation.service";
import { VkKartaMediatorService } from "../../services/vk-karta-mediator.service";
import { VKAtgardHelper } from "../../utils/vkatgard-helper";
import { ImportedGeometry } from "../import-geometries-popup/import-geometries-models";
import { ImportGeometriesPopupComponent } from "../import-geometries-popup/import-geometries-popup.component";
import { SkogsvardMapLayerToggleComponent } from "../skogsvard-map-layer-toggle/skogsvard-map-layer-toggle.component";
import { VkKartaAtgardPopupComponent } from "../vk-karta-atgard-popup/vk-karta-atgard-popup.component";
import { VkKartaCollectorPopupComponent } from "../vk-karta-collector-popup/vk-karta-collector-popup.component";
import { VkKartaSkogsvardPopupComponent } from "../vk-karta-skogsvard-popup/vk-karta-skogsvard-popup.component";
@Component({
    selector: "rask-vk-karta",
    templateUrl: "./vk-karta.component.html",
    styleUrls: ["./vk-karta.component.scss"],
    providers: [
        GeometryClientValidationService,
        MapImportService
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MapComponent,
        MapFastighetComponent,
        MapCollectorComponent,
        MapDrawComponent,
        MapAbortDrawComponent,
        MapMeasureComponent,
        MapFullscreenComponent,
        MapLayersComponent,
        SkogsvardMapLayerToggleComponent,
        VkKartaCollectorPopupComponent,
        ImportGeometriesPopupComponent
    ]
})
export class VkKartaComponent implements OnInit, AfterViewInit, OnDestroy {
    mapsettings: IMapSettings;
    customDrawGroups: any;
    refresh = new Subject<IRenderParams>();
    refreshMapSettings = new Subject<IMapSettings>();
    filter = new Subject<IFilterParams>();
    map: IMapComponent;
    kartPopupEvent$ = new BehaviorSubject<IKartgeometriEvent>(null);
    @ViewChild("mapcontainer", { static: true }) mapcontainer: ElementRef;
    @ViewChild("collector", { static: true }) collectorRef: ElementRef;
    protected useSpringfieldLayer = false;
    protected collectorSearchGeometry: string;
    protected kartgeometrier: Geometri[];
    protected importLayerReady$: Observable<boolean>;
    protected visaMapFileImport = false;
    private geometrier: Geometrier;
    private isDrawActive = false;
    private _kontraktEntity: EntityDTO;
    private readonly ngUnsubscribe$ = new Subject<void>();
    private kontrakt: Kontrakt;
    private atgarder: Atgard[];
    private ritunderlag: VirkeskopDokumentInfoDTO[];
    private readonly throttledRender = debounce(this.render, 100);
    private resizeDebounceHandle: DebouncedFunc<any>;
    private virtualSkogsvardLayerActive: ISkogsvardVirtualLayer;
    private selectedSkogsvardGeometry: IMapGeometry;
    private mapGotoGeometry: IMapGeometry;
    private isStinaLayerLoaded = false;
    private isArbetsorderLoaded = false;
    private selectedArbetsorderGuidList: string[] = [];
    private readonly allLayersReady$ = new ReplaySubject<void>(1);

    private readonly renderer = inject(Renderer2);
    private readonly kontraktCommandService = inject(KontraktCommandService);
    private readonly kartaService = inject(KartaService);
    private readonly mapDrawService = inject(MapDrawService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly vkKartaMediatorService = inject(VkKartaMediatorService);
    private readonly geometryClientValidationService = inject(GeometryClientValidationService);
    private readonly featureService = inject(FeatureService);
    private readonly avtalMediator = inject(AvtalMediatorService);
    private readonly spatialService = inject(SpatialService);
    private readonly terraformerService = inject(TerraformerService);
    private readonly allSkogsvardTradslagService = inject(AllSkogsvardTradslagService);
    private readonly kontraktRitunderlagService = inject(KontraktRitunderlagService);
    private readonly isReadonlyBase = toSignal(this.kontraktMediatorService.isReadonly$);
    private readonly isReadonly = computed(() => this.isReadonlyBase() || !this.canEdit());

    @Input()
    set entity(entity: EntityDTO) {
        if (entity) {
            this._kontraktEntity = entity;
            if (!this._kontraktEntity?.id) {
                this.initNew();
            }
        }
    }
    get entity(): EntityDTO {
        return this._kontraktEntity;
    }

    private readonly canEdit = canEdit();

    constructor() {

        this.mapsettings = {
            id: "virkeskop",
            height: "100%",
            mapVerticalStart: null,
            center: [
                16.1932,
                62.2315
            ],
            zoom: 0,
            baselayers: [
                {
                    id: "metriabakgrund",
                    visible: true
                },
                {
                    id: "metriaorto",
                    visible: true
                }
            ],
            featurelayers: [
                {
                    id: KartaFeaturelayers.Historto,
                    visible: false
                },
                {
                    id: KartaFeaturelayers.Infrarodorto,
                    visible: false
                },
                { id: KartaFeaturelayers.Satellitbilder },
                { id: KartaFeaturelayers.Drone },
                { id: KartaFeaturelayers.SmittadZonSjv },
                {
                    id: KartaFeaturelayers.Collector,
                    visible: false
                },
                {
                    id: KartaFeaturelayers.Arboreal,
                    visible: true
                },
                {
                    id: KartaFeaturelayers.Virkeskop,
                    visible: true
                },
                {
                    id: KartaFeaturelayers.Virkeskop_historiska,
                    visible: false
                },
                {
                    id: KartaFeaturelayers.MetriaFastighetsgranser,
                    visible: true
                },
                {
                    id: KartaFeaturelayers.Restriktioner,
                    visible: true
                },
                {
                    id: KartaFeaturelayers.FornKulturminnen,
                    visible: true
                },
                {
                    id: KartaFeaturelayers.VerkGemAdminGrans,
                    visible: true
                },
                {
                    id: KartaFeaturelayers.VerkGemLLD,
                    visible: true
                },
                {
                    id: KartaFeaturelayers.VerkGemFastigheter,
                    visible: true
                },
                {
                    id: KartaFeaturelayers.VerkGemSamfalligheter,
                    visible: true
                },
                {
                    id: KartaFeaturelayers.VerkGemLKF,
                    visible: true
                },
                {
                    id: KartaFeaturelayers.VerkGemFaktaExternVindkraft,
                    visible: true
                },
                {
                    id: KartaFeaturelayers.VerkGemFaktaBelastningar,
                    visible: true
                },
                {
                    id: KartaFeaturelayers.Vagar,
                    visible: true
                },
                {
                    id: KartaFeaturelayers.VFS_PlaneradVag,
                    visible: true
                },
                {
                    id: KartaFeaturelayers.Laserprodukter,
                    visible: true
                },
                {
                    id: KartaFeaturelayers.EgenSkog,
                    visible: true
                },
                {
                    id: KartaFeaturelayers.StinaKorstrak,
                    visible: false
                },
                {
                    id: KartaFeaturelayers.StinaGrotpunkter,
                    visible: false
                },
                {
                    id: KartaFeaturelayers.StinaHansyn,
                    visible: false
                },
                {
                    id: KartaFeaturelayers.StinaEgenritadeObjekt,
                    visible: false
                },
                {
                    id: KartaFeaturelayers.SKS_Avverkningsanmalningar,
                    visible: false
                },
                {
                    id: KartaFeaturelayers.SKS_UtfordAvverkning,
                    visible: false
                },
                {
                    id: KartaFeaturelayers.Renskotselomraden,
                    visible: false
                }
            ],
            scaleBar: true,
            measure: false,
            coords: true,
            zoomButtons: true,
            onMapReady: (map) => this.mapReady(map),
            onAllLayersReady: (map) => this.allLayersReady(map),
            onLayerReady: (map, layerid) => this.layerReady(map, layerid),
            onMapPan: (map, zoom, scale, center, extent, stationary) => this.onMapPan(map, zoom, scale, center, extent, stationary),
            onMapClick: (mouse, hitlist) => this.onMapClick(mouse, hitlist),
            drawLayers: [],
            mediaLayers: [],
            icons: [
                PlaneraKartaIcons.Kojplats,
                PlaneraKartaIcons.Avlagg,
                PlaneraKartaIcons.Larmpunkt
            ]
        } as IMapSettings;

        this.mapsettings.drawLayers.push({
            id: VkKartaLayerNames.virkeskopsatgardLayer,
            menuOrder: 1,
            title: "Åtgärder",
            geometries: [],
            visible: true,
            readonly: true,
            selectable: false,
            showGeometriesMenu: true,
            hideInLayerList: true,
            hideMenuCounter: true,
            customLayerOrderWeight: 29,
            onClick: (m, g) => {
                this.clickAtgard(m, g);
                return false;
            },
            onHoverIn: (g, m) => { this.hoverIn(g); },
            onHoverOut: (g, m) => { this.hoverOut(g); }
        });
        this.mapsettings.drawLayers.push({
            id: VkKartaLayerNames.levvirkeLayer,
            title: "Åtgärder, leveransvirke",
            geometries: [],
            visible: true,
            readonly: true,
            selectable: false,
            showGeometriesMenu: true,
            hideInLayerList: true,
            onClick: (m, g) => this.clickKartgeometri(m, g)
        });

        this.mapsettings.drawLayers.push({
            id: VkKartaLayerNames.importLayer,
            title: "Manuell import",
            geometries: [],
            visible: true,
            hideInDrawMenu: true,
            hideInLayerList: true,
            readonly: true,
            selectable: false,
            showGeometriesMenu: true
        });

        this.mapsettings.drawLayers.push(this.createGeometryLayer(Kartgeometrityper.Drivningspunkt, "point"));
        this.mapsettings.drawLayers.push(this.createGeometryLayer(Kartgeometrityper.Drivningslinje, "polyline"));
        this.mapsettings.drawLayers.push(this.createGeometryLayer(Kartgeometrityper.Vag, "polyline"));
        this.mapsettings.drawLayers.push(this.createGeometryLayer(Kartgeometrityper.HansynYta, "polygon"));
        this.mapsettings.drawLayers.push(this.createGeometryLayer(Kartgeometrityper.HansynPunkt, "point"));
        this.mapsettings.drawLayers.push(this.createGeometryLayer(Kartgeometrityper.HansynLinje, "polyline"));
        this.mapsettings.drawLayers.push(this.createGeometryLayer(Kartgeometrityper.AllmanLinje, "polyline"));
        this.mapsettings.drawLayers.push(this.createGeometryLayer(Kartgeometrityper.AllmanPunkt, "point"));
    }

    ngAfterViewInit(): void {
        const height = window.innerHeight;
        setTimeout(() => {
            this.adjustMapHeight();
            this.renderer.setStyle(this.collectorRef.nativeElement, "max-height", `${height / 2}px`);
        }, 20);
    }

    ngOnInit(): void {
        this.featureService.getFeatureActive("VisaSpringfield")
            .pipe(takeUntil(this.ngUnsubscribe$)
            ).subscribe((visaSpringfieldEnabled: boolean) => {
                if (visaSpringfieldEnabled) {
                    this.mapsettings.featurelayers.push(
                        {
                            id: KartaFeaturelayers.Springfield,
                            visible: true
                        }
                    );
                }
            });

        this.featureService.getFeatureActive("RedigeraAnvisningyta")
            .pipe(takeUntil(this.ngUnsubscribe$)
            ).subscribe((redigeraAnvisningytaEnabled: boolean) => {
                if (redigeraAnvisningytaEnabled) {
                    this.mapsettings.drawLayers.push(this.createGeometryLayer(Kartgeometrityper.AnvisningYta, "polygon"));
                    this.refreshMapSettings.next(this.mapsettings);
                }
            });

        this.geometryClientValidationService.initSubscriptions();
        this.refreshMapSettings.next(this.mapsettings);

        this.insertSkogsvardLayer();
        this.refreshMapSettings.next(this.mapsettings);

        this.insertFPVMediaLayer();

        this.refreshMapSettings.next(this.mapsettings);

        const completeAllFn = () => this.completeAll();
        this.avtalMediator.registerOnSaveCallback(completeAllFn);

        this.avtalMediator.mapGoto$
            .pipe(takeUntil(this.ngUnsubscribe$), filter(g => !isNil(g)))
            .subscribe(geometry => {
                this.mapGotoGeometry = geometry;
                this.avtalMediator.clearMapGoto();
            });

        this.vkKartaMediatorService.popupGeometri$
            .pipe(filter(geometry => !isNil(geometry)))
            .subscribe(geometry => {
                this.clickKartgeometri(null, geometry);
                this.map?.gotoGeometries([geometry], true);
            });

        this.avtalMediator.save$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(avtal => this.saving());
        this.kontraktMediatorService.kontrakt$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(kontrakt => this.init(kontrakt));
        this.kontraktMediatorService.atgardRemoved$
            .pipe(takeUntil(this.ngUnsubscribe$), filter(a => ObjectUtils.hasValue(a)))
            .subscribe(atgard => this.atgardRemoved(atgard));
        this.kontraktMediatorService.ritunderlagRemoved$
            .pipe(takeUntil(this.ngUnsubscribe$), filter(r => ObjectUtils.hasValue(r)))
            .subscribe(ritunderlag => this.ritunderlagRemoved(ritunderlag));
        this.vkKartaMediatorService.atgardAdded$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(geometri => this.atgardAdded(geometri));
        this.vkKartaMediatorService.atgardGeometriAdded$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(geometri => this.insertGeometryAndCreateCommand(geometri));
        this.vkKartaMediatorService.atgardGeometriChanged$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(geometri => this.updateGeometryAndCreateCommand(geometri));
        this.vkKartaMediatorService.atgardGeometriDeleted$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(geometri => this.deleteGeometryAndCreateCommand(geometri));
        this.vkKartaMediatorService.kontraktGeometriAdded$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(geometri => this.insertGeometryAndCreateCommand(geometri));
        this.vkKartaMediatorService.kontraktGeometriChanged$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(geometri => this.updateGeometryAndCreateCommand(geometri));
        this.vkKartaMediatorService.kontraktGeometriDeleted$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(geometri => this.deleteGeometryAndCreateCommand(geometri));
        this.vkKartaMediatorService.atgardDrivningspunktAdded$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(geometri => this.insertGeometryAndCreateCommand(geometri));
        this.vkKartaMediatorService.atgardDrivningspunktChanged$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(geometri => this.updateGeometryAndCreateCommand(geometri));
        this.vkKartaMediatorService.atgardDrivningspunktDeleted$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(geometri => this.deleteGeometryAndCreateCommand(geometri));
        this.kontraktMediatorService.atgarder$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(atgarder => {
                this.atgarder = atgarder;
                this.syncAtgarder();
                this.setupAtgardButtons();
                this.updateDrawMenu();
            });
        this.kontraktMediatorService.ritunderlag$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(ritunderlag => {
                this.ritunderlag = ritunderlag;
                this.setupRitunderlagButtons();
            });

        this.kartPopupEvent$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(event => {
            if (event?.action === MapEvents.geometriUpdated) {
                this.vkKartaMediatorService.updateGeometryByEvent(event);
            } else if (event?.action === MapEvents.geometriRemoved) {
                this.vkKartaMediatorService.deleteGeometryByEvent(event);
            }
        });

        this.vkKartaMediatorService.activeSkogsvardVirtualLayer$
            .pipe(combineLatestWith(this.allLayersReady$), takeUntil(this.ngUnsubscribe$))
            .subscribe(([skogsvardVirtualLayer]) => {
                if (!skogsvardVirtualLayer || !this.geometrier) {
                    return;
                }
                // Om man togglar skogsvårdsvyer så avbryter vi eventuella pågående sketchritningar
                // Vi stänger även alla popups
                // Detta för att annars hamnar kartan i ett konstigt tillstånd där ytorna ibland finns och inte
                this.map.getSketch()?.cancelSketch();
                this.map.closePopups();

                if (this.virtualSkogsvardLayerActive) {
                    const skogsvardsGeometrierToHide = this.geometrier.skogsvardGeometrier
                        .filter(g => g.atgardAtgardsgrupp === this.virtualSkogsvardLayerActive.group)
                        .filter(g => !isNil(g.mapGeometry?.wkt));

                    skogsvardsGeometrierToHide.forEach(g => g.mapGeometry.visible = false);
                    this.map.deletePreview(skogsvardsGeometrierToHide.map(x => `${x.atgardGuid}-text`));
                }

                if (this.selectedSkogsvardGeometry) {
                    if (skogsvardVirtualLayer.active) {
                        const existingAtgard = this.geometrier.skogsvardGeometrier.find(x => x.atgardGuid === this.selectedSkogsvardGeometry.id);
                        if (existingAtgard.atgardAtgardsgrupp === skogsvardVirtualLayer.group) {
                            this.map.deletePreview([`${this.selectedSkogsvardGeometry.id}-text`]);
                            this.selectedSkogsvardGeometry = null;
                        }
                    } else {
                        this.vkKartaMediatorService.deselectSkogsvardGeometry();
                    }
                }

                if (skogsvardVirtualLayer.active) {
                    this.virtualSkogsvardLayerActive = skogsvardVirtualLayer;

                    const skogsvardsGeometrierToShow = this.geometrier.skogsvardGeometrier
                        .filter(g => g.atgardAtgardsgrupp === skogsvardVirtualLayer.group)
                        .filter(g => !isNil(g.mapGeometry?.wkt));

                    skogsvardsGeometrierToShow.forEach(g => g.mapGeometry.visible = true);
                    const skogsvardLabels = skogsvardsGeometrierToShow.map(g => this.getSkogsvardTradslagLabel(g.atgardGuid));

                    this.map.preview(skogsvardLabels);

                } else {
                    this.virtualSkogsvardLayerActive = null;
                }
                this.map.refreshGeometries(VkKartaLayerNames.skogsvardatgardLayer);
            });

        this.vkKartaMediatorService.highlight$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(hover => {
                if (!hover || !this.kontrakt) {
                    return;
                }
                if (hover.isOver) {
                    const atgardGeometries = this.geometrier?.atgardGeometrier
                        .filter(x => hover.guid.find(hg => hg === x.guid))
                        .filter(x => ObjectUtils.hasValue(x.mapGeometry?.id) && ObjectUtils.hasValue(x.mapGeometry?.wkt))
                        .map(x => x.mapGeometry);
                    if (atgardGeometries?.length > 0) {
                        this.map?.highlightGeometries(VkKartaLayerNames.virkeskopsatgardLayer, atgardGeometries);
                    }
                    const skogsvardGeometries = this.geometrier?.skogsvardGeometrier
                        .filter(x => hover.guid.find(hg => hg === x.guid))
                        .filter(x => ObjectUtils.hasValue(x.mapGeometry?.id) && ObjectUtils.hasValue(x.mapGeometry?.wkt))
                        .map(x => x.mapGeometry);
                    if (skogsvardGeometries?.length > 0) {
                        this.map?.highlightGeometries(VkKartaLayerNames.skogsvardatgardLayer, skogsvardGeometries);
                    }
                } else {
                    this.map?.clearAllHighlights();

                }
            });

        this.vkKartaMediatorService.selectedSkogsvardGeometry$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((nextMapGeom) => {
                if (nextMapGeom && this.selectedSkogsvardGeometry && nextMapGeom?.id === this.selectedSkogsvardGeometry?.id) {
                    // Om man försöker selecta en redan selectad geometri
                    return;
                }
                if (!nextMapGeom && !this.selectedSkogsvardGeometry) {
                    return;
                }

                const localChangeVisibilityForGeometry = (atgardGuid: string, visibility: boolean) => {
                    const existingAtgard = this.geometrier.skogsvardGeometrier.find(x => x.atgardGuid === atgardGuid);
                    if (existingAtgard && this.virtualSkogsvardLayerActive?.group !== existingAtgard.atgardAtgardsgrupp) {
                        if (ObjectUtils.hasValue(existingAtgard.mapGeometry?.id) && ObjectUtils.hasValue(existingAtgard.mapGeometry?.wkt)) {
                            existingAtgard.mapGeometry.visible = visibility;
                        }
                        this.map.refreshGeometries(VkKartaLayerNames.skogsvardatgardLayer);
                    }
                };

                if (this.selectedSkogsvardGeometry) {
                    this.map.deletePreview([`${this.selectedSkogsvardGeometry.id}-text`]);
                    localChangeVisibilityForGeometry(this.selectedSkogsvardGeometry.id, false);
                }

                this.selectedSkogsvardGeometry = nextMapGeom;

                if (nextMapGeom) {
                    const updatedGraphic = this.getSkogsvardTradslagLabel(nextMapGeom.id);
                    this.map.updatePreview([updatedGraphic], true);

                    localChangeVisibilityForGeometry(nextMapGeom.id, true);
                }
            });

        this.allSkogsvardTradslagService.tradslagChanged$
            .pipe(
                withLatestFrom(this.vkKartaMediatorService.activeSkogsvardVirtualLayer$, this.vkKartaMediatorService.selectedSkogsvardGeometry$),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(([
                atgardTradslagChangedGuid,
                skogsvardVirtualLayer,
                selectedSkogsvardGeometry
            ]) => {
                if (!this.map || !this.geometrier || (!skogsvardVirtualLayer && !selectedSkogsvardGeometry)) {
                    return;
                }

                if (skogsvardVirtualLayer) {
                    const previewGraphics = this.geometrier.skogsvardGeometrier
                        .filter(x => x.atgardGuid === atgardTradslagChangedGuid)
                        .filter(x => x.atgardAtgardsgrupp === skogsvardVirtualLayer.group)
                        .filter(x => ObjectUtils.hasValue(x.mapGeometry?.id) && ObjectUtils.hasValue(x.mapGeometry?.wkt))
                        .map(x => this.getSkogsvardTradslagLabel(x.atgardGuid));
                    this.map.updatePreview(previewGraphics, false);
                }
                if (selectedSkogsvardGeometry) {
                    const previewGraphics = this.geometrier.skogsvardGeometrier
                        .filter(x => x.atgardGuid === atgardTradslagChangedGuid)
                        .filter(x => ObjectUtils.hasValue(x.mapGeometry?.id) && ObjectUtils.hasValue(x.mapGeometry?.wkt))
                        .map(x => this.getSkogsvardTradslagLabel(x.atgardGuid));
                    this.map.updatePreview(previewGraphics, false);
                }

            });
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    @HostListener("window:resize", ["$event"]) onWindowResize(event) {
        this.adjustMapHeightDebounced();
    }

    adjustMapHeightDebounced(): void {
        if (ObjectUtils.isNullOrUndefined(this.mapcontainer?.nativeElement) || ObjectUtils.isNullOrUndefined(this.map)) {
            return;
        }

        if (this.resizeDebounceHandle) {
            this.resizeDebounceHandle.cancel();
        }
        this.resizeDebounceHandle = debounce(() => this.adjustMapHeight(), 300);
        this.resizeDebounceHandle();
    }

    adjustMapHeight(): void {
        const parentHeightPx = `${this.mapcontainer?.nativeElement?.offsetParent?.clientHeight}px`;
        this.mapsettings.height = parentHeightPx;
        this.map?.setMapHeight(parentHeightPx);
    }

    syncAtgarder(): void {
        this.selectedArbetsorderGuidList = [];
        this.atgarder?.forEach(a => {
            this.vkKartaMediatorService.updateAtgard(this.kontrakt.guid, a);
            if (!isNil(a.arbetsorder) && !this.selectedArbetsorderGuidList.includes(a.arbetsorder.guid)) {
                this.selectedArbetsorderGuidList.push(a.arbetsorder.guid);
            }
        });
        this.isArbetsorderLoaded = true;
        this.filterArbetsorderUrval(this.selectedArbetsorderGuidList);
    }

    atgardRemoved(atgard: Atgard): void {
        let geometri: Geometri;

        if (atgard.isSkogsvard) {
            geometri = this.vkKartaMediatorService.skogsvardGeometrier$.value?.find(x => x.atgardGuid === atgard.guid);
            if (geometri) {
                this.vkKartaMediatorService.deleteGeometry(VkKartaLayerNames.skogsvardatgardLayer, this.kontrakt.guid, geometri.guid);
            }
        } else {
            geometri = this.vkKartaMediatorService.atgardGeometrier$.value?.find(x => x.atgardGuid === atgard.guid);
            if (geometri) {
                const layerId = this.kontrakt.isLeveransvirke ? VkKartaLayerNames.levvirkeLayer : VkKartaLayerNames.virkeskopsatgardLayer;
                this.vkKartaMediatorService.deleteGeometry(layerId, this.kontrakt.guid, geometri.guid);
            }
        }
    }

    ritunderlagRemoved(ritunderlag: VirkeskopDokumentInfoDTO): void {
        const layerId = this.kontraktRitunderlagService.getLagerId(ritunderlag.id);
        this.map?.removeMediaLayer(layerId);
    }

    createGeometryLayer(typeName: string, geometryType: GeometryType): IMapDrawLayer {
        return {
            id: typeName,
            title: typeName,
            visible: true,
            readonly: true,
            geometries: [],
            selectable: false,
            showGeometriesMenu: true,
            hideInLayerList: true,
            onClick: (m, g) => this.clickKartgeometri(m, g),
            drawOptions: [
                {
                    title: `Ny ${typeName}`,
                    type: geometryType,
                    callback: (g) => this.kontraktGeometryCreated(typeName, g),
                    hideMenuOnDraw: true
                }
            ]
        };
    }

    clickAtgard(mouse: IMapMouse, geometry: IMapGeometry): Observable<any> {
        if (!this.isDrawActive && geometry) {
            const layer = this.getDrawLayer(VkKartaLayerNames.virkeskopsatgardLayer);
            const atgard = this.getAtgard(geometry.id);
            const isOldLeveransvirke = atgard?.atgardstyp === Atgardstyper.Leveransvirke &&
                atgard?.hasKompatibilitet(VirkeskopsatgardKompatibilitet.LeveransvirkeInnanGeometri);

            const isPopupEditable = !this.isReadonly() && (atgard?.status !== Planeringsstatus.Tillgangligt || isOldLeveransvirke);
            return this.showPopup(VkKartaAtgardPopupComponent,
                geometry,
                {
                    geometry: geometry,
                    layer: layer,
                    canEdit: isPopupEditable,
                    param: {
                        kontraktId: this._kontraktEntity.guid,
                        atgardId: geometry.id
                    }
                });
        }
        return undefined;
    }

    clickSkogsvard(mouse: IMapMouse, geometry: IMapGeometry): Observable<any> {
        // Om geometrin är satt så måste vi garantera att geometrin visas innan vi kan poppa upp sketchverktyget
        if (geometry) {
            this.map?.clearAllHighlights();
            this.vkKartaMediatorService.selectSkogsvardGeometry(geometry);
        }

        requestAnimationFrame(() => {
            /*
                Om geometrin inte är satt så har man klickat utanför geometriytan och vill färdigställa sketchen:
                    Om denna kod körs på en gång så avbryts sketchen innan förändringen har skett
                    Vi stänger inte aktivt sketchverktyget. Esri känner av om geometrin försvinner från ett lager och då avbryter den verktyget
                    Problemet med avbryt på sketchverktyget är att den då även återställer den tidigare geometrin
                    Deta är ett fulhack egentligen men det finns inget sätt att säga till Esri att det är en close som avses att göras inte en abort.

                    För att lösa detta just nu kör vi en requestAnimationFrame som då väntar en kort stund innan den deselectarGeometrin vilket
                    gör att den då kommer få uppdateringen av geometrin
            */

            if (!geometry) {
                this.vkKartaMediatorService.deselectSkogsvardGeometry();
            }
        });

        if (!this.isDrawActive && geometry) {
            const layer = this.getDrawLayer(VkKartaLayerNames.skogsvardatgardLayer);
            const atgard = this.getAtgard(geometry.id);
            const isPopupEditable = !this.isReadonly() && atgard?.status !== Planeringsstatus.Tillgangligt;
            return this.showPopup(VkKartaSkogsvardPopupComponent,
                geometry,
                {
                    geometry: geometry,
                    layer: layer,
                    canEdit: isPopupEditable,
                    param: {
                        kontraktId: this._kontraktEntity.guid,
                        atgardId: geometry.id
                    }
                });
        }
        return undefined;
    }

    clickKartgeometri(mouse: IMapMouse, geometry: IMapGeometry): boolean {
        const geometri = this.geometrier.atgardGeometrier.find(x => x.atgardDrivningspunkter.find(y => y.guid === geometry?.tag?.guid));
        if (!this.isDrawActive && geometry) {
            this.showKartgeometriPopup(mouse, geometry as IMapGeometryWicket,
                {
                    kontraktId: this._kontraktEntity.guid,
                    geometryId: geometry?.id,
                    atgardGuid: geometri?.atgardGuid
                });
        }
        return false;
    }

    init(kontrakt: Kontrakt): void {
        if (isNil(kontrakt)) {
            return;
        }
        this.kontrakt = kontrakt;
        this.toggleSkogsvardInDrawMenu();
        this.setSkogsvardAtToppInDrawMenu();

        this.vkKartaMediatorService.initForKontrakt(this.kontrakt).pipe(
            takeUntil(this.ngUnsubscribe$)
        ).subscribe({
            next: geometrier => {
                this.geometrier = {
                    kontraktGuid: geometrier.kontraktGuid,
                    kontraktGeometrier: [...geometrier.kontraktGeometrier],
                    atgardGeometrier: [...geometrier.atgardGeometrier],
                    skogsvardGeometrier: [...geometrier.skogsvardGeometrier]
                };

                this.kartgeometrier = [...geometrier.kontraktGeometrier];
                this.syncAtgarder();
            },
            error: () => {
                this.initNew();
            },
            complete: () => {
                this.throttledRenderWhenAllLayersLoaded(this.geometrier);
                this.updateDrawMenu();
            }
        });
    }

    saving(): void {
        this.map.closeWidgets();
        this.map.closePopups();
    }

    initNew(): void {
        this.geometrier = {
            kontraktGuid: this._kontraktEntity.guid,
            atgardGeometrier: [],
            kontraktGeometrier: [],
            skogsvardGeometrier: []
        };

        this.vkKartaMediatorService.setCacheForKontrakt(this._kontraktEntity.guid, this.geometrier);
    }

    onMapClick(mouse: any, hitlist: IHitItem[]): void {
        //
    }

    onMapPan(map: IMapComponent, zoom: number, scale: number, center: any, extent: any, stationary: boolean): void {
        //
    }

    layerReady(map: IMapComponent, layerid: string): void {
        this.map = map;
        switch (layerid) {
            case VkKartaLayerNames.virkeskopsatgardLayer:
                this.throttledRenderWhenAllLayersLoaded(this.geometrier);
                if (!isNil(this.mapGotoGeometry)) {
                    this.map.gotoGeometries([this.mapGotoGeometry]);
                    this.mapGotoGeometry = null;
                }
                break;
            case KartaFeaturelayers.StinaKorstrak:
            case KartaFeaturelayers.StinaGrotpunkter:
            case KartaFeaturelayers.StinaHansyn:
            case KartaFeaturelayers.StinaEgenritadeObjekt:
                this.isStinaLayerLoaded = true;
                break;
        }
    }

    allLayersReady(map: IMapComponent): void {
        this.map = map;
        this.filterArbetsorderUrval(this.selectedArbetsorderGuidList);
        this.allLayersReady$.next();
    }

    mapReady(map: IMapComponent): void {
        this.map = map;
        this.adjustMapHeight();
        this.filterArbetsorderUrval(this.selectedArbetsorderGuidList);
    }

    isNew(): boolean {
        return !this._kontraktEntity?.id;
    }

    isLeveransvirke(): boolean {
        return this.kontraktMediatorService.kontrakt?.affarsform === VirkeskopAffarsformer.Leveransvirke;
    }

    onMapDraw($event): void {
        //
    }

    canDraw(): boolean {
        return true;
    }

    onDrawActive($event) {
        setTimeout(() => {
            this.isDrawActive = $event;
        }, 200);
    }

    hasCollectorGeometries(): boolean {
        return !isNil(this.collectorSearchGeometry);
    }

    onCollectorLasso($event): void {
        this.collectorSearchGeometry = $event;
        if (isNil($event)) {
            return;
        }
    }

    importFromFile(features: ImportedGeometry[]) {
        features.forEach(f => {
            if (f.type === "atgard") {
                this.handleImportForAtgard(f);
            } else if (f.type === "kontrakt") {
                this.handleImportForKontrakt(f);
            } else {
                console.warn("Okänd typ av import feature:", f.type, f);
            }
        });
        this.map.gotoAllOrDefault();
    }

    importFromCollector(collector: ICollectorImported): void {
        if (!collector.continuecollector) {
            this.stopCollector();
            this.map?.closeWidgets();
        }
        this.addFromCollector(collector.kontraktgeometrier);
        this.updateFromCollector(collector.updatedgeometrier);

        this.addOrUpdateFromCollectorAtgard(collector.atgardgeometrier, VkKartaLayerNames.virkeskopsatgardLayer);
        if (this.isLeveransvirke()) {
            this.addOrUpdateFromCollectorAtgard(collector.drivningspunktgeometrier, VkKartaLayerNames.levvirkeLayer);
        }
        this.renderKartgeometrier();
        this.setupAtgardButtons();
        this.updateDrawMenu();
    }

    addFromCollector(collector: ICollectorGeometriWrapper[]) {

        const bulk = collector?.map(c =>
            new GeometryUpdate(
                c.typ,
                this._kontraktEntity.guid,
                (c.geometri as any).geometry?.wkt,
                null,
                this.getUniqueGuid((c.geometri as any).guid,
                    this.geometrier.kontraktGeometrier),
                c.geometri));
        this.vkKartaMediatorService.updateGeometries(bulk);
    }

    updateFromCollector(collector: ICollectorSubtyper[]) {

        const bulk = collector?.map(g =>
            new GeometryUpdate(
                g.subtyp1,
                this._kontraktEntity.guid,
                (g as Kartgeometri).geometri,
                null,
                (g as Kartgeometri).guid,
                g as Kartgeometri));
        this.vkKartaMediatorService.updateGeometries(bulk);
    }

    addOrUpdateFromCollectorAtgard(collector: any[], typ: string) {
        const importCandidates = [];
        collector?.filter(coll => coll.selectedAtgard?.guid).forEach(collectorGeometri => {

            const antal = collector?.filter(x => x.selectedAtgard?.guid === collectorGeometri.selectedAtgard?.guid);
            if (antal.length > 1) {
                (collectorGeometri as unknown as IKartgeometri).barighet = null;
                (collectorGeometri as unknown as IKartgeometri).ytstruktur = null;
                (collectorGeometri as unknown as IKartgeometri).lutning = null;
                (collectorGeometri as unknown as IKartgeometri).direktutbyteTradslag = null;
                (collectorGeometri as unknown as IKartgeometri).importkalla = null;
            }

            importCandidates.push(new GeometryUpdate(
                typ,
                this._kontraktEntity.guid,
                collectorGeometri.geometry.wkt,
                collectorGeometri.selectedAtgard.guid,
                collectorGeometri.guid,
                collectorGeometri
            ));
        });
        this.vkKartaMediatorService.updateGeometries(importCandidates);
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onImportingFastighet($event): void {

    }

    cancel($event) {
        this.stopCollector();
    }

    stopCollector() {
        this.collectorSearchGeometry = null;
    }

    completeAll() {
        const sketch = this.map?.getSketch();
        if (sketch?.sketchState === "active") {
            sketch?.complete();
        }
        this.map?.closePopups();
        this.map?.closeWidgets();
    }

    onChangeLayer(layer: string) {
        this.useSpringfieldLayer = layer === CollectorImportKalla.Springfield;
    }

    protected onVisaMapFileImport() {
        this.visaMapFileImport = true;
    }

    private handleImportForAtgard(feature: ImportedGeometry) {
        let geometri = this.geometrier.atgardGeometrier.find(x => x.atgardGuid === feature.atgardGuid);
        let layerName = VkKartaLayerNames.virkeskopsatgardLayer;
        if (geometri == null) {
            geometri = this.geometrier.skogsvardGeometrier.find(x => x.atgardGuid === feature.atgardGuid);
            layerName = VkKartaLayerNames.skogsvardatgardLayer;
        }
        geometri.setNewGeometri({ wkt: feature.wktGeometry });
        const atgard = this.atgarder.find(x => x.guid === geometri.guid);

        geometri.updateFromAtgard(atgard);
        this.vkKartaMediatorService.atgardGeometriAdded$.next(geometri);
        this.vkKartaMediatorService.updateGeometry(layerName,
            this._kontraktEntity.guid,
            geometri.atgardGuid,
            geometri.guid,
            feature.wktGeometry,
            geometri.kartgeometri);
    }

    private handleImportForKontrakt(feature: ImportedGeometry) {
        const guid = GuidUtils.newGuid();
        const geometry = this.vkKartaMediatorService.addKontraktGeometri(this._kontraktEntity.guid, guid, feature.subtype1, null, { wkt: feature.wktGeometry });
        geometry.kartgeometri.subtyp2 = feature.subtype2;
        geometry.kartgeometri.subtyp3 = feature.subtype3;

        this.vkKartaMediatorService.kontraktGeometriAdded$.next(geometry);

        const layer = this.getDrawLayer(geometry.mapGeometry.tag.subtyp1);

        const payload: IEventData = {
            geometri: geometry.kartgeometri,
            geoid: geometry.kartgeometri.id,
            layer: layer,
            param: {
                kontraktId: this.kontrakt.guid,
                geometryId: guid
            }
        };

        // Jackar in i kartpopupEvent$ för att följa samma flöde som om man skulle ändra typ i hänsynsområde popup
        this.kartPopupEvent$.next({
            payload,
            action: MapEvents.geometriUpdated
        });
    }

    private filterArbetsorderUrval(aoGuidList: string[]) {
        if (ObjectUtils.isNullOrUndefined(this.map) || !this.isStinaLayerLoaded || !this.isArbetsorderLoaded) {
            return;
        }

        if (aoGuidList.length === 0) {
            this.hideStinaLayers(true);
        } else {
            this.hideStinaLayers(false);
        }

        const guidList = aoGuidList.map((item) => `'${item}'`).join(",");

        const filterExpression = `WORKORDERGUID IN (${guidList})`;
        this.filter.next({
            layerId: KartaFeaturelayers.StinaKorstrak,
            filterExpression: filterExpression
        });
        this.filter.next({
            layerId: KartaFeaturelayers.StinaGrotpunkter,
            filterExpression: filterExpression
        });
        this.filter.next({
            layerId: KartaFeaturelayers.StinaHansyn,
            filterExpression: filterExpression
        });
        this.filter.next({
            layerId: KartaFeaturelayers.StinaEgenritadeObjekt,
            filterExpression: filterExpression
        });
    }

    private hideStinaLayers(hideInLayerList: boolean) {
        this.mapsettings.featurelayers.find(x => x.id === KartaFeaturelayers.StinaKorstrak).hideInLayerList = hideInLayerList;
        this.mapsettings.featurelayers.find(x => x.id === KartaFeaturelayers.StinaGrotpunkter).hideInLayerList = hideInLayerList;
        this.mapsettings.featurelayers.find(x => x.id === KartaFeaturelayers.StinaHansyn).hideInLayerList = hideInLayerList;
        this.mapsettings.featurelayers.find(x => x.id === KartaFeaturelayers.StinaEgenritadeObjekt).hideInLayerList = hideInLayerList;
        this.refreshMapSettings.next(this.mapsettings);
    }

    private getSkogsvardTradslagLabel(atgardGuid: string): IMapGeometryWicket {
        const atgardGeometry = this.geometrier.skogsvardGeometrier.find(x => x.atgardGuid === atgardGuid);
        const atgardMapGeometry = atgardGeometry.mapGeometry;
        const atgPolygon = this.spatialService.convertToGraphic(atgardMapGeometry) as Polygon;
        const centrumkoordinatForAtgard = this.spatialService.centroid(atgPolygon);
        const centrumkoordinatForAtgardAsWKT = this.terraformerService.arcGisToWkt(centrumkoordinatForAtgard);
        const tradslagForAtgard = this.allSkogsvardTradslagService.getTradslagForAtgard(atgardGuid);
        const tradslagStr = tradslagForAtgard.join(", ");
        const geomToAdd: IMapGeometryWicket = {
            id: `${atgardGuid}-text`,
            wkt: centrumkoordinatForAtgardAsWKT,
            symbolText: tradslagStr,
            styleName: "skogsvardTradslag",
            visible: true
        };
        return geomToAdd;
    }

    private insertFPVMediaLayer() {
        this.mapsettings.mediaLayers.push({
            id: VkKartaLayerNames.ritunderlagLayer,
            title: "Ritunderlag",
            visible: true,
            mediaOptions: []
        });
    }

    private insertSkogsvardLayer() {
        this.mapsettings.drawLayers.push({
            id: VkKartaLayerNames.skogsvardatgardLayer,
            title: "Skogsvård",
            geometries: [],
            visible: true,
            readonly: true,
            selectable: false,
            showGeometriesMenu: true,
            allowInvisibleGeometries: true,
            hideInLayerList: true,
            hideMenuCounter: true,
            customLayerOrderWeight: 28,
            onClick: (m, g) => {
                this.clickSkogsvard(m, g);
                return false;
            },
            onHoverIn: (g, m) => {
                this.hoverIn(g);
            },
            onHoverOut: (g, m) => {
                this.hoverOut(g);
            }
        });
    }

    private toggleSkogsvardInDrawMenu() {
        const skogsvardLayer = this.mapsettings.drawLayers.find(l => l.id === VkKartaLayerNames.skogsvardatgardLayer);
        const anvisningLayer = this.mapsettings.drawLayers.find(l => l.id === Kartgeometrityper.AnvisningYta);

        if (anvisningLayer) {
            anvisningLayer.hideInDrawMenu = this.kontrakt.isSkogsvardFPVKontrakt ? false : true;
        }

        if (this.kontrakt.showSkogsvard) {
            skogsvardLayer.hideInDrawMenu = false;
        } else {
            skogsvardLayer.hideInDrawMenu = true;
        }
    }

    private setSkogsvardAtToppInDrawMenu() {
        const skogsvardLayer = this.mapsettings.drawLayers.find(l => l.id === VkKartaLayerNames.skogsvardatgardLayer);
        if (this.kontrakt.isAnySkogsvard) {
            skogsvardLayer.menuOrder = 1;
        }
    }

    private showPopup(component: Type<any>, geometry, data: any) {
        if (!geometry) {
            return undefined;
        }
        return this.map.showPopupOnGeometry(component, geometry, data);
    }

    private showKartgeometriPopup(mouse, geometry: IMapGeometryWicket, param: any, layer?: IMapDrawLayer) {
        let data: IKartgeometriData = null;
        if (geometry?.tag) {
            let drawLayer = layer ? layer : this.mapsettings.drawLayers.find(l => l.geometries?.find(g => g.id === geometry.id));

            if (!drawLayer) {
                drawLayer = this.getDrawLayer(geometry.tag.geometri.subtyp1);
            }

            data = this.getKartgeometriPopupData(geometry, drawLayer, param);
        }
        return this.showPopup(KartgeometriPopupComponent, geometry, data);
    }

    private getKartgeometriPopupData(mapGeometry: IMapGeometryWicket, layer: IMapDrawLayer, param: any): IKartgeometriData {
        return {
            geometri: mapGeometry.tag,
            mapGeometry: mapGeometry,
            kategori: KartgeometriKategori.Virkeskop,
            event$: this.kartPopupEvent$,
            canEdit: !this.isReadonly(),
            isLeveransvirke: this.isLeveransvirke(),
            layer: layer,
            param: param,
            removeGraphicCB: this.removeGraphic
        };
    }

    private removeGraphic(payload: any, cb: () => void): any {
        cb();
        return true;
    }

    private updateDrawMenu(layerid?: string): void {
        if (ObjectUtils.isNullOrUndefined(this.geometrier) ||
            ObjectUtils.isNullOrUndefined(this.map)) {
            return;
        }
        requestAnimationFrame(() => {
            this.refreshAtgarder(this.geometrier);
            if (ObjectUtils.hasValue(layerid)) {
                this.map.refreshSubComponents(layerid);
            } else {
                this.mapsettings.drawLayers.forEach(l => this.map.refreshSubComponents(l.id));
            }
        });
    }

    private async toggleMediaLayer(dokumentId: number) {
        const lagerId = this.kontraktRitunderlagService.getLagerId(dokumentId);
        const lager = this.getMediaSubLayer(lagerId);

        if (lager.selected) {
            if (lager.extent) {
                this.map?.showMediaLayer(lagerId);
            } else {
                const res = await this.vkKartaMediatorService.getRitunderlagKoordinaterCache(lager.dokumentId);

                lager.extent = new Extent({
                    xmin: res.xmin,
                    ymin: res.ymin,
                    xmax: res.xmax,
                    ymax: res.ymax,
                    spatialReference: this.spatialService.getSpatialReference()
                });
                this.map?.showMediaLayer(lagerId);

                const centerX = (res.xmin + res.xmax) / 2;
                const centerY = (res.ymin + res.ymax) / 2;
                this.map?.gotoCoordinate(centerX, centerY, 10000);
            }
        } else {
            this.map?.hideMediaLayer(lagerId);
        }
    }

    private setupRitunderlagButtons() {
        const layer = this.getMediaLayer(VkKartaLayerNames.ritunderlagLayer);
        const selectedDokument = layer.mediaOptions.filter(f => f.selected).map(f => f.dokumentId);

        layer.mediaOptions = [];

        layer.mediaOptions.push({
            title: "Hantera ritunderlag",
            callback: () => this.kontraktMediatorService.openRitunderlagDialog(),
            selectable: false,
            id: `${VkKartaLayerNames.ritunderlagLayer}-hantera`
        });

        this.ritunderlag.forEach(ritunderlag => {
            layer.mediaOptions.push({
                extent: null,
                imgUrl: this.kontraktRitunderlagService.getRitunderlagUrl(ritunderlag.id),
                selected: selectedDokument.includes(ritunderlag.id),
                title: this.kontraktRitunderlagService.getRitunderlagTitel(ritunderlag.filnamn),
                dokumentId: ritunderlag.id,
                selectable: true,
                minScale: 120000,
                callback: () => this.toggleMediaLayer(ritunderlag.id),
                id: this.kontraktRitunderlagService.getLagerId(ritunderlag.id)
            });
        });
    }

    private setupAtgardButtons(): void {
        if (ObjectUtils.isNullOrUndefined(this.geometrier) ||
            ObjectUtils.isNullOrUndefined(this.atgarder)) {
            return;
        }
        if (this.isLeveransvirke()) {
            this.customDrawGroups = [];
            this.renderAtgarderKartgeometrier();
            this.customDrawGroups = this.getLeveransvirkeDrawGroups();
            this.setupAtgardButtonsForAtgardLayer(
                this.atgarder.filter(atg => !VKAtgardHelper.isAtgardsGruppSkogsvard(atg.atgardsgrupp)),
                this.geometrier.atgardGeometrier,
                false);
        } else {
            let skogsvardAtgarder = this.atgarder.filter(atg => VKAtgardHelper.isAtgardsGruppSkogsvard(atg.atgardsgrupp));
            const ovrigaAtgarder = without(this.atgarder, ...skogsvardAtgarder);
            skogsvardAtgarder = skogsvardAtgarder.filter(atg => !atg.isPlantleverans);

            this.setupAtgardButtonsForAtgardLayer(ovrigaAtgarder, this.geometrier.atgardGeometrier);
            this.setupAtgardButtonsForSkogsvardLayer(skogsvardAtgarder, this.geometrier.skogsvardGeometrier);
        }
    }

    private setupAtgardButtonsForAtgardLayer(atgarder: Atgard[], atgardGeometrier: Geometri[], clearDrawOptions = true) {
        const layer = this.getDrawLayer(VkKartaLayerNames.virkeskopsatgardLayer);
        layer.drawOptions = [];

        if (clearDrawOptions) {
            this.customDrawGroups = undefined;
        }

        atgarder.forEach((atg, atgIndex) => {
            const geometri = atgardGeometrier.find(x => x.atgardGuid === atg.guid);
            if (ObjectUtils.isNullOrUndefined(geometri?.kartgeometri?.geometri) &&
                ObjectUtils.isNullOrUndefined(geometri?.mapGeometry?.wkt)) {
                layer.drawOptions.push({
                    title: `${atgIndex + 1} - ${atg.beteckning} ${atg.atgardstyp}`,
                    type: "polygon",
                    hideMenuOnDraw: true,
                    callback: (mapGeometry) => this.atgardGeometriCreated(geometri, mapGeometry)
                });
            }
        });
    }

    private atgardGeometriCreated(geometri: Geometri, mapGeometry: IMapGeometryWicket): void {
        geometri.setNewGeometri(mapGeometry);
        const index = this.geometrier.atgardGeometrier.findIndex(x => x.atgardGuid === geometri.guid);
        mapGeometry.label = `${index + 1} - ${mapGeometry.label}`;
        this.vkKartaMediatorService.atgardGeometriAdded$.next(geometri);
        this.vkKartaMediatorService.updateGeometry(VkKartaLayerNames.virkeskopsatgardLayer,
            this._kontraktEntity.guid,
            geometri.atgardGuid,
            geometri.guid,
            mapGeometry.wkt,
            geometri.kartgeometri);
    }

    private setupAtgardButtonsForSkogsvardLayer(atgarder: Atgard[], atgardGeometrier: Geometri[]) {
        const layer = this.getDrawLayer(VkKartaLayerNames.skogsvardatgardLayer);
        layer.drawOptions = [];
        this.customDrawGroups = undefined;

        // Om kontrakt är viol2 och inte FTV eller kontraktet är readonly eller om vacskontraktet är avräknat/avslutat så lås
        const disabledSkapaSkogsvard = (!this.kontrakt.viol3 && !this.kontrakt.isSkogsvardFPVKontrakt) ||
            this.isReadonlyBase() || this.kontrakt.isVacsAvraknatOrAvslutat;

        if(!disabledSkapaSkogsvard) {
            layer.drawOptions.push({
                title: `Ny Skogsvård`,
                type: "polygon",
                callback: () => this.kontraktMediatorService.openOneOfSkogsvardCreateDialog(),
                manuallyHandledCallback: true,
                hideMenuOnDraw: true,
                drawMenuIcon: "far fa-hand-holding-seedling"
            });
        }

        atgarder.forEach((atg, atgIndex) => {
            const geometri = atgardGeometrier.find(x => x.atgardGuid === atg.guid);
            if (ObjectUtils.isNullOrUndefined(geometri?.kartgeometri?.geometri) &&
                ObjectUtils.isNullOrUndefined(geometri?.mapGeometry?.wkt)) {
                layer.drawOptions.push({
                    title: `${atgIndex + 1} - ${atg.beteckning} ${atg.atgardstyp}`,
                    type: "polygon",
                    hideMenuOnDraw: true,
                    callback: (mapGeometry) => this.skogsvardatgardGeometriCreated(geometri, mapGeometry)
                });
            }
        });
    }

    private skogsvardatgardGeometriCreated(geometri: Geometri, mapGeometry: IMapGeometryWicket): void {
        this.vkKartaMediatorService.activateVirtualSkogsvardLayer({
            group: geometri.atgardAtgardsgrupp,
            active: true
        });
        geometri.setNewGeometri(mapGeometry);
        const skogsvardsAtgard = this.atgarder.find(x => x.guid === geometri.guid);
        geometri.updateFromAtgard(skogsvardsAtgard);
        this.vkKartaMediatorService.atgardGeometriAdded$.next(geometri);
        this.vkKartaMediatorService.updateGeometry(VkKartaLayerNames.skogsvardatgardLayer,
            this._kontraktEntity.guid,
            geometri.atgardGuid,
            geometri.guid,
            mapGeometry.wkt,
            geometri.kartgeometri);
    }

    private kontraktGeometryCreated(geometriTyp: string, mapGeometry: IMapGeometryWicket): void {
        const guid = GuidUtils.newGuid();
        const g = this.vkKartaMediatorService.addKontraktGeometri(this._kontraktEntity.guid, guid, geometriTyp, null, mapGeometry);
        this.vkKartaMediatorService.kontraktGeometriAdded$.next(g);
        this.showKartgeometriPopup(null, g.mapGeometry, {
            kontraktId: this._kontraktEntity.guid,
            geometryId: g.mapGeometry.id
        });
    }

    private atgardDrivningspunktCreated(geometriTyp: string, mapGeometry: IMapGeometryWicket, atgard: Geometri): void {
        const guid = GuidUtils.newGuid();
        const g = this.vkKartaMediatorService.addAtgardDrivningspunktGeometri(this._kontraktEntity.guid, guid, geometriTyp, mapGeometry, atgard);
        this.vkKartaMediatorService.atgardDrivningspunktAdded$.next(g);
        const layer = this.getDrawLayer("levvirkelayer");
        this.showKartgeometriPopup(
            null,
            g.mapGeometry,
            {
                isLeveransvirke: true,
                kontraktId: this._kontraktEntity.guid,
                geometryId: g.mapGeometry.id,
                atgardGuid: atgard.atgardGuid
            },
            layer
        );
    }

    private throttledRenderWhenAllLayersLoaded(geometrier: Geometrier) {
        this.allLayersReady$.pipe(
            take(1)
        ).subscribe(() => {
            this.throttledRender(geometrier);
        });
    }

    private render(geometrier: Geometrier): void {
        if (!geometrier) {
            return;
        }
        this.refreshAtgarder(geometrier);

        this.renderKartgeometrier();

        this.map?.gotoAll();
    }

    private refreshAtgarder(geometrier: Geometrier) {
        if (!this.map) {
            return;
        }
        const atgardGeometrier = geometrier.atgardGeometrier.filter(g => !isNil(g.mapGeometry?.wkt)).map(x => x.mapGeometry);
        const skogsvardGeometrier = this.getSkogsvardVirtualLayerGeometries(geometrier.skogsvardGeometrier);

        this.map.setGeometries(VkKartaLayerNames.virkeskopsatgardLayer, atgardGeometrier);
        this.map.setGeometries(VkKartaLayerNames.skogsvardatgardLayer, skogsvardGeometrier);

        this.setupAtgardButtons();
    }

    private getSkogsvardVirtualLayerGeometries(skogsvardGeometrier: Geometri[]): IMapGeometryWicket[] {
        return skogsvardGeometrier
            .filter(g => !isNil(g.mapGeometry?.wkt))
            .map(x => {
                x.mapGeometry.visible = this.virtualSkogsvardLayerActive?.group === x.atgardAtgardsgrupp;
                return x.mapGeometry;
            });
    }

    private renderKartgeometrier(): void {

        const subtyper = [
            Kartgeometrityper.Drivningspunkt,
            Kartgeometrityper.Drivningslinje,
            Kartgeometrityper.Vag,
            Kartgeometrityper.HansynYta,
            Kartgeometrityper.AnvisningYta,
            Kartgeometrityper.HansynPunkt,
            Kartgeometrityper.HansynLinje,
            Kartgeometrityper.AllmanLinje,
            Kartgeometrityper.AllmanPunkt
        ];

        subtyper.forEach(typ => {
            const subtypgeometrier = this.geometrier.kontraktGeometrier.filter(x => x.kartgeometri.subtyp1 === typ);
            this.renderKartgeometrityp(typ, subtypgeometrier);
        });
    }

    private renderAtgarderKartgeometrier() {
        const drivningspunkter = this.geometrier.atgardGeometrier.reduce((a, b) =>
            a.concat(b.atgardDrivningspunkter ?? []), []);
        this.renderKartgeometrityp(Kartgeometrityper.Drivningspunkt, drivningspunkter, VkKartaLayerNames.levvirkeLayer);
    }

    private renderKartgeometrityp(subtyp: string, geometrier: Geometri[], altlayer: string = null): IMapGeometry[] {
        const mapGeometries = this.kartaService.renderKartgeometrier(geometrier);
        const layer = !isNil(altlayer) ? altlayer : subtyp;
        if (layer && this.mapsettings.drawLayers.some(x => x.id === layer)) {
            this.map?.setGeometries(layer, mapGeometries);
        } else {
            console.warn(`Geometrityp saknar lager ${subtyp}`, geometrier);
        }
        return mapGeometries;
    }

    private insertGeometryAndCreateCommand(geometri: Geometri): IMapGeometryWicket {
        if (geometri?.kontraktGuid !== this._kontraktEntity.guid || ObjectUtils.isNullOrUndefined(this.map)) {
            return undefined;
        }
        const command = this.createAddOrUpdateCommand(geometri);
        command.markAsInsert(); // Annotera den som Insert och inte "Replace"
        if (geometri.parentType === "skogsvard") {
            // atgardsgeometri
            this.map.addGeometries(VkKartaLayerNames.skogsvardatgardLayer, [geometri.mapGeometry]);
            this.kontraktCommandService.update(command);
            this.updateDrawMenu(VkKartaLayerNames.skogsvardatgardLayer);
            return geometri.mapGeometry;
        } else if (geometri.parentType === "atgard") {
            // atgardsgeometri
            this.map.addGeometries(VkKartaLayerNames.virkeskopsatgardLayer, [geometri.mapGeometry]);
            this.kontraktCommandService.update(command);
            this.updateDrawMenu(VkKartaLayerNames.virkeskopsatgardLayer);
            return geometri.mapGeometry;
        } else if (geometri.parentType === "kontrakt") {
            // kontraktgeometri
            const newGeometries = this.kartaService.renderKartgeometri(geometri, geometri.mapGeometry?.geoid, null);
            this.geometrier.kontraktGeometrier.push(geometri);
            this.map.addGeometries(geometri.kartgeometri.subtyp1, newGeometries);
            this.kontraktCommandService.update(command);
            this.updateDrawMenu();
            return newGeometries[0];
        } else if (geometri.parentType === "") {
            // atgard drivningspunkt geometri
            const newGeometries = this.kartaService.renderKartgeometri(geometri, geometri.mapGeometry?.geoid, geometri.atgardGuid);
            const atg = this.geometrier.atgardGeometrier.find(a => a.guid === geometri.atgardGuid);
            atg.addAtgardDrivningspunkt(geometri);
            this.map.addGeometries(VkKartaLayerNames.levvirkeLayer, newGeometries);
            this.kontraktCommandService.update(command);
            this.updateDrawMenu(VkKartaLayerNames.levvirkeLayer);
            return newGeometries[0];
        }
        return null;
    }

    private createAddOrUpdateCommand(geometri: Geometri): AddOrUpdateGeometryCommand {
        if (geometri.parentType === "atgard") {
            // atgardsgeometri
            return new AddOrUpdateGeometryCommand(geometri.atgardGuid, geometri, this._kontraktEntity.guid);
        } else if (geometri.parentType === "skogsvard") {
            // skogsvårdsgeometri
            return new AddOrUpdateGeometryCommand(geometri.atgardGuid, geometri, this._kontraktEntity.guid);
        } else if (geometri.parentType === "kontrakt") {
            // kontraktgeometri
            return new AddOrUpdateGeometryCommand(geometri.guid, geometri, this._kontraktEntity.guid);
        } else if (geometri.parentType === "") {
            // atgard drivningspunkt geometri
            return new AddOrUpdateGeometryCommand(geometri.guid, geometri, this._kontraktEntity.guid, geometri.atgardGuid);
        } else {
            throw new Error("Geometri saknar parentType");
        }
    }

    private updateGeometryAndCreateCommand(geometri: Geometri): void {
        if (!geometri || !this.map) {
            return;
        }
        const geometryAndLayer = this.find(geometri);
        if (!isNil(geometryAndLayer?.mapGeometry)) {
            geometryAndLayer.mapGeometry.wkt = geometri?.mapGeometry?.wkt;
            this.map.updateGeometry(geometryAndLayer.mapGeometry, geometryAndLayer.layer.id, false);
            const command = this.createAddOrUpdateCommand(geometri);
            this.kontraktCommandService.update(command);
            this.renderKartgeometrier();
            this.updateDrawMenu(geometryAndLayer.layer.id);
        }
    }

    private deleteGeometryAndCreateCommand(geometri: Geometri): void {
        if (!geometri || !this.map) {
            return;
        }
        const geometryAndLayer = this.find(geometri);
        this.deleteGeometryFromMap(geometryAndLayer);

        let atgardsGuid: string;

        // atgardsgeometri, kontraktgeometri, kontraktgeometri -> här är mapGeometry.id (GUID) samma som atgardsGuid eller Guid på kontraktsgeometri
        // Fungerar inte med primarykey på commands (Delete/AddOrUpdate) om man inte har samma parameteruppsättning och värden.
        if (geometri.parentType === "atgard" ||
            geometri.parentType === "skogsvard" ||
            geometri.parentType === "kontrakt") {
            atgardsGuid = null;
        } else {
            // atgardsGuid för hantering av drivningspunkt geometrier
            atgardsGuid = geometri.atgardGuid;
        }

        this.kontraktCommandService.update(new DeleteGeometryCommand(geometryAndLayer.mapGeometry.id, atgardsGuid, geometri.kontraktGuid, geometri.parentType));
        this.updateDrawMenu(geometryAndLayer.layer.id);
    }

    private deleteGeometryFromMap(geometryAndLayer: IMapGeometryLayer): void {
        // try delete from map
        this.map.updateGeometry(geometryAndLayer.mapGeometry, geometryAndLayer.layer.id, true);
        // try delete or clear from mapsettings
        if (geometryAndLayer.geometri.parentType === "atgard") {
            // only clear wkt
            geometryAndLayer.geometri.kartgeometri.geometri = null;
            geometryAndLayer.mapGeometry.wkt = null;
        } else {
            // remove from mapsettings-draw-layer
            const geoIndex = geometryAndLayer.layer.geometries.indexOf(geometryAndLayer.mapGeometry);
            if (geoIndex > -1) {
                geometryAndLayer.layer.geometries.splice(geoIndex, 1);
            }
            // remove from kontrakt-geometrier
            const kartGeometriIndex = this.geometrier.kontraktGeometrier.indexOf(geometryAndLayer.geometri);
            if (kartGeometriIndex > -1) {
                this.geometrier.kontraktGeometrier.splice(kartGeometriIndex, 1);
            }

            // Remove from atgarder-geometrier
            this.geometrier.atgardGeometrier
                .forEach(x => {
                    const index = x.atgardDrivningspunkter.indexOf(geometryAndLayer.geometri);
                    if (index > -1) {
                        x.atgardDrivningspunkter.splice(index, 1);
                    }
                });

        }
    }

    private find(geometri: Geometri): IMapGeometryLayer {
        if (isNil(geometri) || geometri?.kontraktGuid !== this._kontraktEntity.guid) {
            return null;
        }
        let geometriId = "";
        let layerId = "";
        if (geometri.parentType === "atgard") {
            // atgardgeometri
            layerId = VkKartaLayerNames.virkeskopsatgardLayer;
            geometriId = geometri.atgardGuid;
        } else if (geometri.parentType === "kontrakt") {
            // kontraktgeometri
            layerId = geometri.mapGeometry.tag.subtyp1;
            geometriId = geometri.mapGeometry.id;
        } else if (geometri.parentType === "skogsvard") {
            layerId = VkKartaLayerNames.skogsvardatgardLayer;
            geometriId = geometri.mapGeometry.id;
        } else if (geometri.parentType === "") {
            layerId = VkKartaLayerNames.levvirkeLayer;
            geometriId = geometri.mapGeometry.id;
        }
        const layer = this.getDrawLayer(layerId);
        let mapGeometry = layer.geometries.find(x => x.id === geometriId) as IMapGeometryWicket;
        if (!mapGeometry) {
            console.warn(`find: Geometry ${geometri.parentType} not found in layer ${layerId}: ${geometriId}`);
            mapGeometry = geometri.mapGeometry;
        }
        return {
            mapGeometry: mapGeometry,
            geometri: geometri,
            layer: layer
        };
    }

    private atgardAdded(geometri: Geometri): void {
        if (isNil(geometri) || ObjectUtils.isNullOrUndefined(this.geometrier) || geometri.kontraktGuid !== this._kontraktEntity.guid) {
            return;
        }
        if (this.geometrier.kontraktGuid === this._kontraktEntity.guid) {
            if (geometri.parentType === "atgard" && !this.geometrier.atgardGeometrier.some(g => g.atgardGuid === geometri.atgardGuid)) {
                this.geometrier.atgardGeometrier.push(geometri);
            }
            if (geometri.parentType === "skogsvard" && !this.geometrier.skogsvardGeometrier.some(g => g.atgardGuid === geometri.atgardGuid)) {
                this.geometrier.skogsvardGeometrier.push(geometri);
            }
            this.updateDrawMenu(VkKartaLayerNames.virkeskopsatgardLayer);
            this.updateDrawMenu(VkKartaLayerNames.skogsvardatgardLayer);
        }
    }

    private getLeveransvirkeDrawGroups() {
        const layer = this.getDrawLayer(VkKartaLayerNames.levvirkeLayer);
        const getItemsForGeometri = (geometri: Geometri) => {
            if (isNil(geometri.atgardDrivningspunkter)) {
                return [];
            }

            return geometri.atgardDrivningspunkter
                .filter(g => !isNil(g.kartgeometri?.geometri))
                .map(g => {
                    const renderedKartgeometri = layer.geometries.find(rg => rg.id === g.guid);
                    if (renderedKartgeometri) {
                        return this.mapDrawService.getDrawItemFromMapGeometry(renderedKartgeometri, layer);
                    } else {
                        return {};
                    }
                });
        };

        const atgardLayer = this.mapsettings.drawLayers.find(l => l.id === VkKartaLayerNames.virkeskopsatgardLayer);

        // Då "customGroups" används så ersätts befintliga lager vilket gör att vi behöver populera åtgärdslagret själva för leveransvirke
        const drawGroups = this.mapDrawService.getDefaultDrawGroupsFromLayers([atgardLayer]);
        const atgardGroups = this.geometrier.atgardGeometrier.map(a => ({
            id: a.guid,
            title: a.atgardAtgardsnamn ?? a.atgardBeteckning,
            optionTargetLayer: layer,
            drawOptions: [
                {
                    title: `Ny ${Kartgeometrityper.Drivningspunkt}`,
                    type: "point",
                    callback: (g) => this.atgardDrivningspunktCreated(Kartgeometrityper.Drivningspunkt, g, a),
                    hideMenuOnDraw: true
                }
            ],
            items: getItemsForGeometri(a)
        } as IDrawGroup));

        drawGroups.push(...atgardGroups);

        return drawGroups;
    }

    private getUniqueGuid(guid: string, geometries: Geometri[]): string {
        let newguid = guid;
        if (geometries.some(g => g.guid === guid)) {
            newguid = GuidUtils.newGuid();
        }
        return newguid;
    }

    private getAtgard(atgardGuid: string): Atgard {
        return this.kontraktMediatorService.atgarder.find(x => x.guid === atgardGuid);
    }

    private hoverIn(g: IMapGeometry): void {
        this.vkKartaMediatorService.hover(g);
    }

    private hoverOut(g: IMapGeometry): void {
        this.vkKartaMediatorService.hover(g, false);
    }

    private getDrawLayer(layerid: string): IMapDrawLayer {
        return this.mapsettings.drawLayers.find(l => l.id === layerid);
    }

    private getMediaLayer(layerid: string): IMapMediaLayerContainer {
        return this.mapsettings.mediaLayers.find(l => l.id === layerid);
    }

    private getMediaSubLayer(id: string): IMapMediaLayerImage {
        return this.mapsettings.mediaLayers.flatMap(ml => ml.mediaOptions).find(x => x.id === id);
    }
}
